import { useId, type ReactNode } from 'react';
import { Checkbox, Label, Tooltip, TooltipContent, TooltipTrigger, cn } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import type { ReportSelectionSection } from '../ReportSelectionDialog';
import { FieldDescriptionTip } from '../FieldDescriptionTip';
import { matchingFieldSections } from './fieldSearch';

/** One searchable checklist group (dimensions or metrics) of the field rail. */
export function FieldGroup<TId extends string>({
  title,
  sections,
  selected,
  search,
  disabled,
  isDisabled,
  disabledReason,
  onChange,
  renderAside,
  renderBelow,
}: {
  title: string;
  sections: readonly ReportSelectionSection<TId>[];
  selected: readonly TId[];
  search: string;
  disabled: boolean;
  isDisabled: (candidate: TId) => boolean;
  disabledReason: (candidate: TId) => string;
  onChange: (value: TId[]) => void;
  renderAside?: (id: TId, checked: boolean) => ReactNode;
  renderBelow?: (id: TId, checked: boolean) => ReactNode;
}) {
  const headingId = useId();
  const selectedIds = new Set(selected);
  const visibleSections = matchingFieldSections(sections, search);
  if (!visibleSections.length) return null;

  return (
    <section aria-labelledby={headingId} className="space-y-1">
      <div className="text-muted-foreground flex items-center gap-2 px-2 text-xs">
        <h2 id={headingId} className="font-semibold">
          {title}
        </h2>
        <span className="ml-auto font-medium">
          {dashboardT('reportExplorer.fields.selected', { count: selected.length })}
        </span>
        <button
          type="button"
          disabled={disabled || selected.length === 0}
          className="text-primary cursor-pointer font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline"
          aria-label={dashboardT('reportExplorer.fields.clear', { group: title })}
          onClick={() => {
            onChange([]);
          }}
        >
          {dashboardT('reportExplorer.selection.clear')}
        </button>
      </div>
      {visibleSections.map((section) => (
        <div key={section.id}>
          <h3 className="text-muted-foreground px-2 pt-2 pb-1 text-xs">
            {dashboardT(section.labelKey)}
          </h3>
          {section.options.map((option) => {
            const checked = selectedIds.has(option.id);
            const incompatible = !checked && isDisabled(option.id);
            // Scoped per group: some fields (CR ID, Order) are both a dimension and a metric.
            const id = `${headingId}-${option.id}`;
            const field = (
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <Checkbox
                  id={id}
                  checked={checked}
                  disabled={disabled || incompatible}
                  onCheckedChange={(next) => {
                    onChange(
                      next === true
                        ? [...selected, option.id]
                        : selected.filter((item) => item !== option.id),
                    );
                  }}
                />
                <Label
                  htmlFor={id}
                  className="min-w-0 flex-1 cursor-pointer truncate text-[13px] font-normal peer-disabled:cursor-not-allowed"
                >
                  {option.label}
                </Label>
              </div>
            );
            return (
              <div key={option.id}>
                <div
                  className={cn(
                    'flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 transition-colors',
                    checked ? 'bg-accent' : 'hover:bg-muted',
                  )}
                >
                  {incompatible ? (
                    <Tooltip>
                      <TooltipTrigger asChild>{field}</TooltipTrigger>
                      <TooltipContent side="right" className="max-w-64">
                        <DisabledReason reason={() => disabledReason(option.id)} />
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    field
                  )}
                  {option.description ? (
                    <FieldDescriptionTip
                      label={option.label}
                      description={option.description}
                      side="right"
                      className="hover:text-foreground cursor-help"
                    />
                  ) : null}
                  {renderAside?.(option.id, checked)}
                </div>
                {renderBelow?.(option.id, checked)}
              </div>
            );
          })}
        </div>
      ))}
    </section>
  );
}

/** Rendered only while the tooltip is open, so the conflict search stays off the render path. */
function DisabledReason({ reason }: { reason: () => string }) {
  return <>{reason()}</>;
}
